/* Aria Robotics — Kontakt + Datenschutz + Impressum. Ported aus page-legal.jsx.
 * (Phase 0: Kontaktformular ist Demo. Phase 4 verdrahtet echten Versand.) */
import * as React from 'react';
import { Container, Section, Btn, Chip, Panel, IconTile, Icon, Field, ImageSlot } from '@/ui';
import { AriaData } from '@/data';
import { pb } from '@/lib/pb';
import { COLLECTIONS } from '@/lib/types';

const D = AriaData;

export function Contact() {
  const [sent, setSent] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    const fd = new FormData(e.currentTarget);
    // Honeypot: bots fill hidden field → silently accept without storing.
    if (fd.get('website')) { setSent(true); return; }
    const name = `${fd.get('vorname') ?? ''} ${fd.get('nachname') ?? ''}`.trim();
    setBusy(true);
    try {
      await pb.collection(COLLECTIONS.contacts).create({
        name,
        email: String(fd.get('email') ?? ''),
        subject: String(fd.get('anliegen') ?? ''),
        message: String(fd.get('nachricht') ?? ''),
      });
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Senden fehlgeschlagen. Bitte später erneut versuchen.');
    } finally {
      setBusy(false);
    }
  };
  const infos: [string, string, string][] = [
    ['map-pin', D.contact.company, `${D.contact.street}, ${D.contact.city}`],
    ['mail', 'E-Mail', D.contact.email],
    ['newspaper', 'Presse', D.contact.press],
    ['phone', 'Telefon', `${D.contact.phone} · ${D.contact.hours}`],
  ];
  return (
    <div>
      <header style={{ background: 'var(--ink)', color: '#fff', backgroundImage: 'radial-gradient(640px 380px at 82% 14%, rgba(74,144,226,0.22), transparent 60%)' }}>
        <Container>
          <div style={{ padding: '76px 0', maxWidth: 720 }}>
            <Chip tone="inverse" dot>Kontakt</Chip>
            <h1 style={{ fontSize: 'clamp(34px, 5vw, 54px)', fontWeight: 800, letterSpacing: '-0.03em', margin: '18px 0 0', lineHeight: 1.04 }}>Schreiben Sie uns.</h1>
            <p style={{ fontSize: 19, fontWeight: 300, color: 'rgba(255,255,255,0.8)', marginTop: 14, maxWidth: '48ch' }}>Ob Vorbestellung, Frage oder Presse — wir antworten persönlich, meist am selben Werktag.</p>
          </div>
        </Container>
      </header>

      <Section tone="page">
        <Container>
          <div className="ar-split" style={{ display: 'grid', gridTemplateColumns: '1.1fr 0.9fr', gap: 56, alignItems: 'start' }}>
            {/* Formular */}
            <Panel variant="default" padding={36}>
              {sent ? (
                <div style={{ textAlign: 'center', padding: '32px 8px' }}>
                  <span style={{ display: 'inline-flex', width: 60, height: 60, borderRadius: '50%', alignItems: 'center', justifyContent: 'center', background: 'rgba(74,144,226,0.14)', color: 'var(--primary-200)' }}><Icon name="check" size={30} /></span>
                  <h3 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-100)', margin: '20px 0 8px' }}>Vielen Dank.</h3>
                  <p style={{ fontSize: 16, color: 'var(--text-200)', lineHeight: 1.6, maxWidth: '40ch', margin: '0 auto' }}>Ihre Nachricht ist bei uns eingegangen. Ein Mensch aus dem Aria-Team meldet sich persönlich bei Ihnen zurück.</p>
                  <div style={{ marginTop: 24 }}><Btn variant="secondary" onClick={() => setSent(false)}>Neue Nachricht</Btn></div>
                </div>
              ) : (
                <form onSubmit={submit}>
                  <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-100)', margin: '0 0 4px' }}>Nachricht senden</h2>
                  <p style={{ fontSize: 14, color: 'var(--text-200)', margin: '0 0 24px' }}>Felder mit * sind Pflichtangaben.</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <Field label="Vorname" name="vorname" required placeholder="Maria" />
                    <Field label="Nachname" name="nachname" required placeholder="Beispiel" />
                  </div>
                  <div style={{ marginTop: 16 }}><Field label="E-Mail" name="email" type="email" required placeholder="maria@beispiel.de" /></div>
                  <div style={{ marginTop: 16 }}>
                    <label htmlFor="f-anliegen" style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-100)', display: 'block', marginBottom: 6 }}>Anliegen</label>
                    <select id="f-anliegen" name="anliegen" style={{ width: '100%', fontFamily: 'var(--font-sans)', fontSize: 16, color: 'var(--text-100)', background: 'var(--surface-page)', padding: '12px 14px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-default)', boxSizing: 'border-box' }}>
                      <option>Vorbestellung Nestor 01</option>
                      <option>Allgemeine Frage</option>
                      <option>Service & Support</option>
                      <option>Presse</option>
                      <option>Partnerschaft</option>
                    </select>
                  </div>
                  <div style={{ marginTop: 16 }}><Field label="Ihre Nachricht" name="nachricht" textarea rows={5} required placeholder="Wie können wir helfen?" /></div>
                  <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 18, fontSize: 13.5, color: 'var(--text-200)', lineHeight: 1.5 }}>
                    <input type="checkbox" required style={{ marginTop: 3, accentColor: 'var(--primary-200)' }} />
                    <span>Ich habe die <a href="#/datenschutz" style={{ color: 'var(--primary-200)' }}>Datenschutzerklärung</a> gelesen und stimme der Verarbeitung meiner Angaben zu.</span>
                  </label>
                  {/* Honeypot (visually hidden, bots fill it) */}
                  <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
                    style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} />
                  {error && (
                    <div style={{ marginTop: 16, padding: '10px 12px', background: 'rgba(229,72,77,0.10)', border: '1px solid rgba(229,72,77,0.3)', borderRadius: 'var(--radius-sm)', color: '#e5484d', fontSize: 13.5 }}>{error}</div>
                  )}
                  <div style={{ marginTop: 24 }}><Btn variant="cta" size="lg" type="submit" fullWidth iconRight="send">{busy ? 'Wird gesendet…' : 'Nachricht senden'}</Btn></div>
                  <p style={{ fontSize: 12, color: 'var(--text-200)', textAlign: 'center', marginTop: 12 }}>Ihre Angaben werden vertraulich behandelt und nur zur Bearbeitung Ihrer Anfrage verwendet.</p>
                </form>
              )}
            </Panel>

            {/* Kontaktinfos */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Panel variant="sunken">
                <h3 style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-100)', margin: '0 0 16px' }}>So erreichen Sie uns</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {infos.map(([ic, a, b]) => (
                    <div key={a} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                      <IconTile name={ic} size={40} />
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-100)' }}>{a}</div>
                        <div style={{ fontSize: 14, color: 'var(--text-200)', lineHeight: 1.45 }}>{b}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Panel>
              <div style={{ position: 'relative', aspectRatio: '4/3', borderRadius: 'var(--radius-lg)', overflow: 'hidden', boxShadow: 'var(--shadow-sm)' }}>
                <ImageSlot id="contact-map" shape="rect" placeholder="Karte / Standort Leipzig" style={{ width: '100%', height: '100%' }} />
              </div>
            </div>
          </div>
        </Container>
      </Section>
    </div>
  );
}

function LegalShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <header style={{ background: 'var(--ink)', color: '#fff' }}>
        <Container><div style={{ padding: '64px 0 56px' }}>
          <h1 style={{ fontSize: 'clamp(30px, 4.4vw, 46px)', fontWeight: 800, letterSpacing: '-0.025em', margin: 0 }}>{title}</h1>
        </div></Container>
      </header>
      <Section tone="page">
        <Container narrow>
          <div className="ar-legal" style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>{children}</div>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-200)', marginTop: 40, letterSpacing: '0.03em' }}>Stand: Juli 2026 · Beispieltext für Demonstrationszwecke.</p>
        </Container>
      </Section>
    </div>
  );
}
const H = ({ children }: { children: React.ReactNode }) => <h2 style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--text-100)', margin: '8px 0 0' }}>{children}</h2>;
const P = ({ children }: { children: React.ReactNode }) => <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-body)', margin: 0 }}>{children}</p>;

export function Privacy() {
  return (
    <LegalShell title="Datenschutzerklärung">
      <P>Der Schutz Ihrer Daten ist für Aria Robotics kein Pflichtprogramm, sondern Teil unseres Versprechens. Diese Erklärung informiert Sie darüber, wie wir mit personenbezogenen Daten umgehen.</P>
      <H>Verantwortliche Stelle</H>
      <P>{D.contact.company}, {D.contact.street}, {D.contact.city}, {D.contact.country}. E-Mail: {D.contact.email}.</P>
      <H>Welche Daten wir erheben</H>
      <P>Beim Absenden des Kontaktformulars verarbeiten wir die von Ihnen angegebenen Daten (Name, E-Mail-Adresse, Anliegen und Nachricht), um Ihre Anfrage zu beantworten. Eine Weitergabe an Dritte erfolgt nicht ohne Ihre Einwilligung.</P>
      <H>Daten aus Nestor 01</H>
      <P>Nestor verarbeitet Bild- und Tondaten grundsätzlich lokal in Ihrem Zuhause. Diese Daten verlassen Ihr Zuhause nicht ohne Ihre ausdrückliche Zustimmung. Die Kamera lässt sich jederzeit mechanisch abdecken.</P>
      <H>Ihre Rechte</H>
      <P>Sie haben das Recht auf Auskunft, Berichtigung, Löschung und Einschränkung der Verarbeitung Ihrer Daten sowie auf Datenübertragbarkeit und Widerspruch. Wenden Sie sich dazu jederzeit an {D.contact.email}.</P>
      <H>Einsatz von KI-Systemen</H>
      <P>Für die Erstellung von Inhalten sowie für Funktionen von Nestor setzen wir Systeme künstlicher Intelligenz ein. Wir kennzeichnen KI-generierte Inhalte transparent gemäß der EU-KI-Verordnung (EU AI Act).</P>
    </LegalShell>
  );
}

export function Imprint() {
  return (
    <LegalShell title="Impressum">
      <H>Angaben gemäß § 5 DDG</H>
      <P>{D.contact.company}<br />{D.contact.street}<br />{D.contact.city}<br />{D.contact.country}</P>
      <H>Vertreten durch</H>
      <P>Dr. Lena Hartmann (Geschäftsführerin)</P>
      <H>Kontakt</H>
      <P>Telefon: {D.contact.phone}<br />E-Mail: {D.contact.email}</P>
      <H>Registereintrag</H>
      <P>Eintragung im Handelsregister · Registergericht: Amtsgericht Leipzig · Registernummer: HRB 000000 (Beispiel).</P>
      <H>Umsatzsteuer-ID</H>
      <P>Umsatzsteuer-Identifikationsnummer gemäß § 27 a UStG: DE000000000 (Beispiel).</P>
      <H>Verantwortlich für den Inhalt</H>
      <P>Dr. Lena Hartmann, Anschrift wie oben.</P>
      <H>Hinweis zu KI-generierten Inhalten</H>
      <P>Teile der Inhalte dieser Website wurden mit Unterstützung von KI-Werkzeugen erstellt und redaktionell geprüft. Die Kennzeichnung erfolgt gemäß der EU-KI-Verordnung (EU AI Act).</P>
    </LegalShell>
  );
}
